/**
 * Checks whether a given string is valid JSON.
 *
 * @param jsonString - A string to validate as JSON.
 * @returns `true` if the string is valid JSON, otherwise `false`.
 *
 * @example
 * isValidJSON('{"a": 1}');
 * // → true
 *
 * isValidJSON('{a: 1}');
 * // → false
 *
 * isValidJSON('not JSON');
 * // → false
 */
export const isValidJSON = (jsonString: string) => {
  try {
    JSON.parse(jsonString);
    return true;
  } catch {
    return false;
  }
};

/**
 * Validates whether a given value is a valid JSON string.
 *
 * @param val - The value to validate. Can be of any type.
 * @returns `null` if the value is a valid JSON string,
 *          otherwise an array of error messages.
 *
 * @example
 * validateJson(null);
 * // → null
 *
 * @example
 * validateJson({});
 * // → ['Input is not a string']
 *
 * @example
 * validateJson('{"ok": true}');
 * // → null
 *
 * @example
 * validateJson('not JSON');
 * // → ['Unexpected token o in JSON at position 1']
 */
export const validateJson = (val?: unknown): string[] | null => {
  if (!val) return null;

  const errors: string[] = [];

  // expecting valid JSON to be a string
  // 123, {}, false -- are not valid JSON values
  if (typeof val !== 'string') {
    errors.push('Input is not a string');
    return errors;
  }

  try {
    JSON.parse(val);
  } catch (e) {
    errors.push(e instanceof Error ? e.message : 'Invalid JSON');
  }

  return errors.length ? errors : null;
};
